import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useMutation } from '@apollo/client';
import { gql } from 'graphql-tag';
import { v4 as uuid } from 'uuid';
import {
  isoStringToObject,
  mapRepliconDateToMidnightUTCString
} from '~/modules/common/dates/convert';
import { useMeContext } from '~/modules/me/useMeContext';
import { useResourceUserDetailsContext } from '~/modules/resource-management/resourceUserDetails/ResourceUserDetailsContext';
import { GetCertificatesQuery } from '~/modules/resource-management/resourceUserDetails/hooks/useCertificates';
import { RESOURCE_USER_DETAILS_ACTION } from '~/modules/resource-management/resourceUserDetails/hooks/resourceUserDetailsAction';
import useBulkAcceptUserCertificateNudge from './useBulkAcceptUserCertificateNudge';
import { mapCertificateKey } from './combineCertificates';

const BULK_PUT_USER_CERTIFICATE = gql`
  mutation bulkPutUserCertificate($input: BulkPutUserCertificateInput!) {
    bulkPutUserCertificate(input: $input) {
      certificates {
        id
        uri
        name
        issueDate
        expiryDate
        issuer
        expires
        credentialId
        credentialUrl
      }
      errors {
        certificate {
          uri
          name
          issueDate
          expiryDate
          issuer
        }
        error {
          type
          reason
        }
      }
    }
  }
`;

const mapToCertificateInput = certificate => ({
  id: certificate.id,
  uri: certificate.uri,
  name: certificate.name,
  issuer: certificate.issuer,
  issueDate: certificate.issueDate
    ? mapRepliconDateToMidnightUTCString(certificate.issueDate)
    : null,
  expiryDate: certificate.expiryDate
    ? mapRepliconDateToMidnightUTCString(certificate.expiryDate)
    : null,
  expires: certificate.expires,
  credentialId: certificate.credentialId,
  credentialUrl: certificate.credentialUrl
});

const buildOptimisticResponse = ({ certificates, tenantSlug }) => {
  return {
    bulkPutUserCertificate: {
      certificates: certificates.map(certificate => {
        const uri = `urn:replicon-tenant:${tenantSlug}:user-certificate:temp-${uuid()}`;

        return {
          id: uri,
          uri,
          name: certificate.name,
          issueDate: certificate.issueDate,
          expiryDate: certificate.expiryDate,
          issuer: certificate.issuer,
          expires: certificate.expires,
          credentialId: certificate.credentialId,
          credentialUrl: certificate.credentialUrl,
          __typename: 'Certificate'
        };
      }),
      errors: [],
      __typename: 'BulkPutUserCertificateResult'
    }
  };
};

const updateResourceCertificatesCache = ({
  cache,
  mutationResponse,
  userSlug
}) => {
  const {
    data: {
      bulkPutUserCertificate: { certificates: updatedCertificates }
    }
  } = mutationResponse;

  const { resource: cachedResourceDetails } = cache.readQuery({
    query: GetCertificatesQuery,
    variables: { userSlug, includeExpired: true }
  });

  const { certificates: cachedCertificates } = cachedResourceDetails;

  cache.writeQuery({
    query: GetCertificatesQuery,
    variables: { userSlug, includeExpired: true },
    data: {
      resource: {
        ...cachedResourceDetails,
        certificates: [...cachedCertificates, ...updatedCertificates]
      }
    }
  });
};

export const mapBulkPutUserCertificateErrorsToCertificates = bulkPutUserCertificateErrors =>
  bulkPutUserCertificateErrors.reduce((acc, certificateError) => {
    const { certificate } = certificateError;
    const certificateKey = mapCertificateKey({
      ...certificate,
      issueDate: certificate.issueDate
        ? isoStringToObject(certificate.issueDate)
        : null,
      expiryDate: certificate.expiryDate
        ? isoStringToObject(certificate.expiryDate)
        : null
    });

    acc[certificateKey] = certificate;

    return acc;
  }, {});

export const getCreatedCertificateNudgeIds = ({
  certificates,
  bulkPutUserCertificateErrors
}) => {
  const bulkPutUserCertificateErrorsMap = mapBulkPutUserCertificateErrorsToCertificates(
    bulkPutUserCertificateErrors
  );

  return certificates
    .filter(
      certificate =>
        !bulkPutUserCertificateErrorsMap[mapCertificateKey(certificate)]
    )
    .map(certificate => certificate.nudgeId);
};

export const useOnAcceptCertificateNudges = ({ userUri, userSlug }) => {
  const { tenantSlug } = useMeContext();
  const intl = useIntl();
  const { dispatch } = useResourceUserDetailsContext();

  const [bulkPutUserCertificate] = useMutation(BULK_PUT_USER_CERTIFICATE, {});
  const { bulkAcceptUserCertificateNudge } = useBulkAcceptUserCertificateNudge({
    userUri
  });

  const onAcceptCertificateNudgesComplete = useCallback(
    ({ requestedCertificates, errorCount }) => {
      if (!requestedCertificates?.length) return;

      dispatch({
        type: RESOURCE_USER_DETAILS_ACTION.SET_SNACKBAR_MESSAGE,
        snackbarMessage: errorCount
          ? intl.formatMessage(
              {
                id:
                  'resourceCertificatesAndLicensesCard.suggestionAcceptedError'
              },
              { count: requestedCertificates?.length }
            )
          : requestedCertificates?.length > 1
          ? intl.formatMessage({
              id: 'resourceCertificatesAndLicensesCard.allSuggestionsAccepted'
            })
          : intl.formatMessage(
              { id: 'resourceCertificatesAndLicensesCard.suggestionAccepted' },
              { certificate: requestedCertificates[0].name }
            )
      });
    },
    [intl, dispatch]
  );

  const onAcceptCertificateNudges = useCallback(
    async certificates => {
      const bulkPutUserCertificateInput = certificates.map(
        mapToCertificateInput
      );

      const {
        data: {
          bulkPutUserCertificate: { errors: bulkPutUserCertificateErrors }
        }
      } = await bulkPutUserCertificate({
        variables: {
          input: {
            certificates: bulkPutUserCertificateInput,
            userUri
          }
        },
        optimisticResponse: buildOptimisticResponse({
          certificates,
          tenantSlug
        }),
        update: (cache, mutationResponse) =>
          updateResourceCertificatesCache({
            cache,
            mutationResponse,
            userUri,
            userSlug
          })
      });

      const nudgeIds = getCreatedCertificateNudgeIds({
        certificates,
        bulkPutUserCertificateErrors
      });

      const {
        data: {
          bulkAcceptUserCertificateNudge: bulkAcceptUserCertificateNudgeData
        }
      } = await bulkAcceptUserCertificateNudge(nudgeIds);

      const bulkAcceptUserNudgeErrors =
        bulkAcceptUserCertificateNudgeData?.errors || [];

      onAcceptCertificateNudgesComplete({
        requestedCertificates: certificates,
        errorCount:
          bulkPutUserCertificateErrors.length + bulkAcceptUserNudgeErrors.length
      });
    },
    [
      userUri,
      userSlug,
      tenantSlug,
      bulkPutUserCertificate,
      bulkAcceptUserCertificateNudge,
      onAcceptCertificateNudgesComplete
    ]
  );

  return {
    onAcceptCertificateNudges
  };
};
